import {gql} from '@apollo/client';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants, {PROCESS_TYPES} from 'constants/ModelTypeConstants';
import {
  getRegistrySupportedOligoAwareTypes,
  isSearchableSsiType,
  SchemaInterfaceExtendingSchema,
} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import {groupSchemasByType, SchemasFilterFunction} from 'nav/sidepanel/listing/ItemListingUtil';
import {isModelTypeEnabledForFiltering} from 'nav/sidepanel/listing/SearchSearchParams';
import AssayStore from 'results/AssayStore';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import StorableStore from 'samples/StorableStore';
import * as FilterUtil from 'search/FilterUtil';
import {Filter, FilterKey, MultiValueFilter, SingleValueFilter} from 'search/SearchConstants';
import {fetchStarredStorableSchemas, toggleStorableSchemaStar} from 'stars/StarsReduxActions';
import {toggleTagSchemaStar} from 'tag-schema/TagSchemaActions';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown, {closeDropdown} from 'ui-library/dropdowns/Dropdown';
import {capwords, typeToName} from 'util/NameUtil';
import pickDefined from 'util/pickDefined';

import {TypesFilterSchemasQuery} from './__generated/TypesFilterSchemasQuery';
import {TypesFilterTagSchemasSSIQuery} from './__generated/TypesFilterTagSchemasSSIQuery';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import PrimaryFilterFormSkeleton from './base/PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './base/SecondaryFilterFormSkeleton';
import {TypesMenuSchema} from './TypesAndSchemaFilterTypes';
import TypesAndSchemaMenu, {getAvailableProcessTypes} from './TypesAndSchemaMenu';

export interface TypesAndSchemaFilterFormExtraProps {
  filteredInterfacesByAvailableTypes?: Array<SchemaInterfaceExtendingSchema>;
  isTypeRequired?: boolean;
  availableTypes: Array<string>;
  filterSchemas?: SchemasFilterFunction;
  alwaysApplySchemasFilter?: boolean;
}

interface FilterProps extends BaseFilterFormProps {
  filter: Filter;
  filters: Array<Filter>;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: Filter) => void;
  onChangeFilters: (filters: Array<Filter>) => void;
  onRemove?: () => void;
}

interface GraphqlProps {
  schemasByAvailableType: {[type: string]: Array<TypesMenuSchema>};
  starredTagSchemaIds: Array<string> | null;
}

type Props = FilterProps & TypesAndSchemaFilterFormExtraProps & GraphqlProps;

export interface ToggleSchemaStarFunctions {
  onToggleTagSchemaStar: (tagSchemaId: string) => void;
  onToggleStorableSchemaStar: (storableSchemaId: string) => void;
}

export interface EnhancedSchemaProps extends ToggleSchemaStarFunctions {
  starredSchemaIds: Array<string> | null;
}

interface FetchSchemaProps {
  onFetchStarredStorableSchemas: () => void;
}

type EnhancedProps = EnhancedSchemaProps & FetchSchemaProps;

class TypesAndSchemaFilterFormConnector extends DataConnector<
  Props & TypesAndSchemaFilterFormExtraProps,
  EnhancedProps
> {
  stores = [AssayStore, SampleSchemaStore, StorableStore];

  _handleFetchStarredStorableSchemas = (): void => {
    this.dispatch(fetchStarredStorableSchemas());
  };

  _handleToggleStorableSchemaStar = (storableSchemaId: string): void => {
    this.dispatch(toggleStorableSchemaStar(storableSchemaId));
  };

  enhanceProps(): EnhancedProps {
    const starredStorableSchemaIds = this.reduxState().models.stars.storableSchemaIds;
    const starredSchemaIds =
      this.props.starredTagSchemaIds && starredStorableSchemaIds
        ? [...this.props.starredTagSchemaIds, ...starredStorableSchemaIds]
        : [];

    return {
      onToggleTagSchemaStar: toggleTagSchemaStar,
      onFetchStarredStorableSchemas: this._handleFetchStarredStorableSchemas,
      onToggleStorableSchemaStar: this._handleToggleStorableSchemaStar,
      starredSchemaIds,
    };
  }
}

export class TypesAndSchemaFilterForm extends React.Component<Props & EnhancedProps> {
  dropdown?: Dropdown;

  constructor(props: Props & EnhancedProps) {
    super(props);
    props.onFetchStarredStorableSchemas();
  }

  _findSchema(schemaId: string): TypesMenuSchema | null {
    const schemas = Object.values(this.props.schemasByAvailableType).flatMap(
      (schemasForType) => schemasForType
    );
    return schemas.find((schema) => schema.id === schemaId) || null;
  }

  _findSchemaInterface(interfaceId: string): SchemaInterfaceExtendingSchema | null {
    if (!this.props.filteredInterfacesByAvailableTypes) {
      return null;
    }
    return (
      this.props.filteredInterfacesByAvailableTypes.find(
        (schemaInterface) => schemaInterface.id === interfaceId
      ) || null
    );
  }

  _findSsiName(interfaceId: string): string | null {
    let type = null;
    if (interfaceId === ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID) {
      type = ModelTypeConstants._DO_NOT_USE_OLIGO_CONJUGATE;
    } else if (interfaceId === ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID) {
      type = ModelTypeConstants._DO_NOT_USE_OLIGO_DUPLEX;
    } else if (interfaceId === ANTIBODY_SCHEMA_INTERFACE_ID) {
      type = ModelTypeConstants._DO_NOT_USE_ANTIBODY;
    } else if (interfaceId === EQUIPMENT_SCHEMA_INTERFACE_ID) {
      type = ModelTypeConstants.EQUIPMENT;
    }

    if (type) {
      return capwords(typeToName(type));
    }

    return null;
  }

  _isAllTypes(typesFilter: MultiValueFilter): boolean {
    return Object.keys(this.props.schemasByAvailableType).every(
      (type) => typesFilter.value.includes(type) || isSearchableSsiType(type)
    );
  }

  _getSelectedText(): string | null {
    const schemaFilter = FilterUtil.getFilterByKey(
      this.props.filters,
      FilterKey.SCHEMA
    ) as MultiValueFilter | null;
    const interfaceFilter = FilterUtil.getFilterByKey(
      this.props.filters,
      FilterKey.SCHEMA_INTERFACE
    ) as SingleValueFilter | null;
    // Only display selected schema if exactly one schema is selected
    if (schemaFilter && schemaFilter.value?.length === 1) {
      const schema = this._findSchema(schemaFilter.value[0]);
      return schema ? schema.name : 'Loading...';
    }
    if (interfaceFilter && interfaceFilter.value) {
      const ssiName = this._findSsiName(interfaceFilter.value[0]);
      if (ssiName) {
        return ssiName;
      }

      const schemaInterface = this._findSchemaInterface(interfaceFilter.value[0]);
      return schemaInterface ? schemaInterface.name : 'Loading...';
    }
    const typesFilter = FilterUtil.getFilterByKey(
      this.props.filters,
      FilterKey.TYPES
    ) as MultiValueFilter | null;
    if (typesFilter && typesFilter.value.length > 0) {
      if (
        isEqual(
          _.sortBy(typesFilter.value),
          _.sortBy(
            getRegistrySupportedOligoAwareTypes().filter((type) => isModelTypeEnabledForFiltering(type))
          )
        )
      ) {
        return 'Entity';
      }
      if (
        getAvailableProcessTypes(this.props.schemasByAvailableType).length > 1 &&
        typesFilter.value.every((type) => (PROCESS_TYPES as Array<string>).includes(type))
      ) {
        return 'Task group';
      }
      if (!this._isAllTypes(typesFilter)) {
        return typesFilter.value
          .map((type) => typeToName(type))
          .map((name) => capwords(name))
          .join(', ');
      }
    }
    return null;
  }

  _handleChangeFilters = (filters: Array<Filter>): void => {
    this.props.onChangeFilters(filters);
    if (this.dropdown) {
      closeDropdown(this.dropdown);
    }
  };

  render(): JSX.Element {
    const skeletonProps = pickDefined(this.props, ...BASE_FILTER_FORM_PROP_NAMES);
    const menu = (
      <TypesAndSchemaMenu
        filters={this.props.filters}
        onChangeFilters={this._handleChangeFilters}
        schemasByAvailableType={this.props.schemasByAvailableType}
        filteredInterfacesByAvailableTypes={this.props.filteredInterfacesByAvailableTypes}
        isTypeRequired={this.props.isTypeRequired}
        alwaysApplySchemasFilter={this.props.alwaysApplySchemasFilter}
        // Provided by connect
        onToggleTagSchemaStar={this.props.onToggleTagSchemaStar}
        onToggleStorableSchemaStar={this.props.onToggleStorableSchemaStar}
        starredSchemaIds={this.props.starredSchemaIds}
      />
    );
    const selectedText = this._getSelectedText();
    const dropdown = (
      <Dropdown closeOnInsideClick={true} menu={menu}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          fullWidth={true}
          isDropdown={true}
          size={this.props.buttonSize}
          text={selectedText || 'Select...'}
        />
      </Dropdown>
    );
    if (this.props.filterStyle === FilterStyle.PRIMARY) {
      const typesFilter = FilterUtil.getFilterByKey(
        this.props.filters,
        FilterKey.TYPES
      ) as MultiValueFilter | null;
      const schemaFilter = FilterUtil.getFilterByKey(
        this.props.filters,
        FilterKey.SCHEMA
      ) as MultiValueFilter;
      return (
        <PrimaryFilterFormSkeleton
          {...skeletonProps}
          isActive={Boolean(
            (schemaFilter && schemaFilter.value?.length === 1) ||
              (typesFilter && typesFilter.value.length > 0 && !this._isAllTypes(typesFilter))
          )}
          buttonSize={this.props.buttonSize}
          dropdownRef={(node: Dropdown) => {
            this.dropdown = node;
          }}
          dropdownToggleText={selectedText ? `Type: ${selectedText}` : 'Type'}
          menuOverride={menu}
          tooltipTitle={selectedText}
          dataTestAttrs={{component: 'TypeAndSchemaFilterForm', element: 'typeDropdownButton'}}
        />
      );
    } else {
      if (!this.props.onRemove) {
        throw new Error('Expected props.onRemove to be present when filterStyle is SECONDARY');
      }
      return (
        <SecondaryFilterFormSkeleton
          {...skeletonProps}
          nodeGrid={[[dropdown]]}
          onChangeFilterKey={this.props.onChangeFilterKey}
          selectedFilterKey={this.props.selectedFilterKey}
          onRemove={this.props.onRemove}
        />
      );
    }
  }
}

const TypesAndSchemaFilterFormInnerComponent = TypesAndSchemaFilterFormConnector.connect(
  TypesAndSchemaFilterForm,
  {
    throttleUpdates: true,
    shouldPropagateNextState: (a, b) => !isEqual(a, b),
  }
);

export const TYPES_FILTER_SSI_QUERY = gql`
  query TypesFilterTagSchemasSSIQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    tagSchemas {
      id
      interfaceConfigs {
        interface {
          id
          ... on StaticSchemaInterfaceNode {
            id
          }
        }
      }
    }
  }
`;

export const TYPES_FILTER_SCHEMAS_QUERY = gql`
  query TypesFilterSchemasQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    requestSchemas {
      archiveRecord {
        purpose
      }
      id
      name
    }
    resultSchemas {
      archiveRecord {
        purpose
      }
      id
      name
    }
    runSchemas {
      archiveRecord {
        purpose
      }
      id
      name
    }
    boxSchemas {
      archiveRecord {
        purpose
      }
      id
      name
      registry {
        id
      }
    }
    connectionSchemas {
      archiveRecord {
        purpose
      }
      id
      name
    }
    containerSchemas {
      archiveRecord {
        purpose
      }
      id
      name
      registry {
        id
      }
    }
    locationSchemas {
      archiveRecord {
        purpose
      }
      id
      name
      registry {
        id
      }
    }
    plateSchemas {
      archiveRecord {
        purpose
      }
      id
      name
      registry {
        id
      }
    }
    processTaskSchemasSupportingPlatformFeature(platformFeature: WORKFLOWS_SEARCH) {
      archiveRecord {
        purpose
      }
      id
      name
    }
    processOutputSchemasSupportingPlatformFeature(platformFeature: WORKFLOWS_SEARCH) {
      archiveRecord {
        purpose
      }
      id
      name
    }
    tagSchemas {
      archiveRecord {
        purpose
      }
      folderItemType
      id
      name
      registry {
        id
      }
      sequenceType
      shouldCreateAsOligo
    }
    starredTagSchemas {
      id
    }
    studySchemas {
      id
      name
      archiveRecord {
        purpose
      }
    }
  }
`;

const EMPTY_SCHEMAS: TypesFilterSchemasQuery = {
  boxSchemas: [],
  connectionSchemas: [],
  containerSchemas: [],
  locationSchemas: [],
  plateSchemas: [],
  processTaskSchemasSupportingPlatformFeature: [],
  processOutputSchemasSupportingPlatformFeature: [],
  tagSchemas: [],
  starredTagSchemas: [],
  studySchemas: [],
  requestSchemas: [],
  resultSchemas: [],
  runSchemas: [],
};

export const EMPTY_EXCLUSIVE_SSI_DATA: TypesFilterTagSchemasSSIQuery = {
  tagSchemas: [],
};

export default function TypeAndSchemaFilterFormGraphQLWrapper(
  props: FilterProps & TypesAndSchemaFilterFormExtraProps
): JSX.Element | null {
  let schemasData = EMPTY_SCHEMAS;
  const schemasResult = useBenchlingQuery<TypesFilterSchemasQuery>(TYPES_FILTER_SCHEMAS_QUERY);

  let exclusiveSSIData = EMPTY_EXCLUSIVE_SSI_DATA;
  const exclusiveSSIResult = useBenchlingQuery<TypesFilterTagSchemasSSIQuery>(TYPES_FILTER_SSI_QUERY);

  if (schemasResult.type === QueryResultType.SUCCESS) {
    schemasData = schemasResult.data;
  }

  if (exclusiveSSIResult.type === QueryResultType.SUCCESS) {
    exclusiveSSIData = exclusiveSSIResult.data;
  }

  const schemasFromQuery = groupSchemasByType(schemasData, exclusiveSSIData);
  const schemasByAvailableType = pickDefined(schemasFromQuery, ...props.availableTypes);
  const filteredSchemasByAvailableType = props.filterSchemas
    ? props.filterSchemas(schemasByAvailableType)
    : schemasByAvailableType;

  return (
    <TypesAndSchemaFilterFormInnerComponent
      {...props}
      schemasByAvailableType={filteredSchemasByAvailableType as {[modelType: string]: Array<TypesMenuSchema>}}
      alwaysApplySchemasFilter={!isEqual(schemasByAvailableType, filteredSchemasByAvailableType)}
      starredTagSchemaIds={schemasData.starredTagSchemas.map(({id}) => id)}
    />
  );
}
